/**
 * Walk-Forward Visualization Component
 * Version: 6.2.0
 */
import { useState } from 'react'
import './WalkForwardVisualization.css'

interface WFAnalysisResult {
  config: {
    trainSize: number
    testSize: number
    stepSize: number
    mode: string
  }
  windows: WFWindowResult[]
  aggregateMetrics: WFAggregateMetrics
  bestWindow: number
  worstWindow: number
  overallEquity: number[]
}

interface WFWindowResult {
  windowIndex: number
  startDate: number
  endDate: number
  trainStart: number
  trainEnd: number
  testStart: number
  testEnd: number
  metrics: WFMetrics
  equityCurve: number[]
  trades: WFTrade[]
}

interface WFTrade {
  entryDate: number
  exitDate: number
  entryPrice: number
  exitPrice: number
  type: string
  pnl: number
  returnPct: number
}

interface WFMetrics {
  trainReturn: number
  trainSharpe: number
  trainWinRate: number
  trainMaxDrawdown: number
  testReturn: number
  testSharpe: number
  testWinRate: number
  testMaxDrawdown: number
  wfRatio: number
  returnConsistency: number
  sharpeConsistency: number
}

interface WFAggregateMetrics {
  avgReturn: number
  avgSharpe: number
  avgWinRate: number
  avgDrawdown: number
  totalTrades: number
  winRate: number
  maxDrawdown: number
  sharpeStdDev: number
  returnStdDev: number
}

interface WalkForwardVisualizationProps {
  results: WFAnalysisResult
}

function WalkForwardVisualization({ results }: WalkForwardVisualizationProps) {
  const [selectedWindow, setSelectedWindow] = useState<number | null>(null)
  const [viewMode, setViewMode] = useState<'equity' | 'comparison' | 'metrics'>('equity')

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', { 
      month: 'short', 
      year: '2-digit' 
    })
  }

  const formatPercent = (value: number) => `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`
  
  const formatSharpe = (value: number) => value.toFixed(2)

  // Calculate equity chart dimensions
  const maxEquity = Math.max(...results.overallEquity, 0)
  const minEquity = Math.min(...results.overallEquity, 0)
  const range = maxEquity - minEquity || 1

  const getY = (value: number) => {
    return 100 - ((value - minEquity) / range) * 100
  }

  return (
    <div className="wf-visualization">
      {/* Header */}
      <div className="wf-header">
        <div className="wf-title">
          <span className="wf-icon">📊</span>
          <h3>Walk-Forward Analysis</h3>
        </div>
        <div className="wf-config">
          <span>Train: {results.config.trainSize} periods</span>
          <span>Test: {results.config.testSize} periods</span>
          <span>Mode: {results.config.mode}</span>
          <span>Windows: {results.windows.length}</span>
        </div>
      </div>

      {/* View Toggle */}
      <div className="wf-view-toggle">
        <button 
          className={`toggle-btn ${viewMode === 'equity' ? 'active' : ''}`}
          onClick={() => setViewMode('equity')}
        >
          📈 Equity Curve
        </button>
        <button 
          className={`toggle-btn ${viewMode === 'comparison' ? 'active' : ''}`}
          onClick={() => setViewMode('comparison')}
        >
          📊 Comparison
        </button>
        <button 
          className={`toggle-btn ${viewMode === 'metrics' ? 'active' : ''}`}
          onClick={() => setViewMode('metrics')}
        >
          📋 Metrics
        </button>
      </div>

      {/* Aggregate Metrics */}
      <div className="wf-aggregate">
        <div className="metric-card">
          <span className="metric-label">Avg Return</span>
          <span className={`metric-value ${results.aggregateMetrics.avgReturn >= 0 ? 'positive' : 'negative'}`}>
            {formatPercent(results.aggregateMetrics.avgReturn)}
          </span>
        </div>
        <div className="metric-card">
          <span className="metric-label">Avg Sharpe</span>
          <span className="metric-value">{formatSharpe(results.aggregateMetrics.avgSharpe)}</span>
        </div>
        <div className="metric-card">
          <span className="metric-label">Win Rate</span>
          <span className="metric-value">{(results.aggregateMetrics.winRate * 100).toFixed(1)}%</span>
        </div>
        <div className="metric-card">
          <span className="metric-label">Total Trades</span>
          <span className="metric-value">{results.aggregateMetrics.totalTrades}</span>
        </div>
        <div className="metric-card">
          <span className="metric-label">WF Ratio</span>
          <span className={`metric-value ${results.aggregateMetrics.sharpeStdDev < 0.5 ? 'positive' : 'warning'}`}>
            {formatSharpe(results.aggregateMetrics.avgSharpe / (results.aggregateMetrics.sharpeStdDev || 1))}
          </span>
        </div>
      </div>

      {/* Main Content */}
      <div className="wf-content">
        {viewMode === 'equity' && (
          <div className="wf-equity-chart">
            <svg viewBox="0 0 800 300" className="equity-svg">
              {/* Grid lines */}
              {[0, 25, 50, 75, 100].map(y => (
                <line 
                  key={y} 
                  x1="50" 
                  y1={y * 2.8 + 10} 
                  x2="780" 
                  y2={y * 2.8 + 10}
                  stroke="var(--border-color)"
                  strokeDasharray="4"
                />
              ))}
              
              {/* Zero line */}
              <line 
                x1="50" 
                y1={getY(0) * 2.8 + 10} 
                x2="780" 
                y2={getY(0) * 2.8 + 10}
                stroke="var(--text-muted)"
                strokeWidth="1"
              />
              
              {/* Equity curve */}
              <path
                d={results.overallEquity.map((value, i) => {
                  const x = 50 + (i / results.overallEquity.length) * 730
                  const y = getY(value) * 2.8 + 10
                  return `${i === 0 ? 'M' : 'L'} ${x} ${y}`
                }).join(' ')}
                fill="none"
                stroke="var(--primary)"
                strokeWidth="2"
              />
              
              {/* Window markers */}
              {results.windows.map((_window, i) => {
                const x = 50 + (i / results.windows.length) * 730
                return (
                  <g key={i}>
                    <line 
                      x1={x} 
                      y1="280" 
                      x2={x} 
                      y2="290"
                      stroke="var(--text-muted)"
                      strokeWidth="1"
                    />
                    <text 
                      x={x + 30} 
                      y="285" 
                      fill="var(--text-muted)"
                      fontSize="10"
                    >
                      W{i + 1}
                    </text>
                  </g>
                )
              })}
            </svg>
            <div className="chart-legend">
              <span className="legend-item">📈 Overall Equity</span>
              <span className="legend-item">--- Window Boundaries</span>
            </div>
          </div>
        )}

        {viewMode === 'comparison' && (
          <div className="wf-comparison-chart">
            <div className="comparison-bars">
              {results.windows.map((window, i) => (
                <div 
                  key={i} 
                  className={`window-bar ${i === results.bestWindow ? 'best' : ''} ${i === results.worstWindow ? 'worst' : ''}`}
                  onClick={() => setSelectedWindow(i)}
                >
                  <div className="bar-label">W{i + 1}</div>
                  <div className="bar-container">
                    <div 
                      className="bar-train"
                      style={{ 
                        height: `${Math.min(Math.abs(window.metrics.trainReturn) * 2, 100)}%`,
                        background: 'var(--text-muted)'
                      }}
                      title={`Train: ${formatPercent(window.metrics.trainReturn)}`}
                    />
                    <div 
                      className="bar-test"
                      style={{ 
                        height: `${Math.min(Math.abs(window.metrics.testReturn) * 2, 100)}%`,
                        background: window.metrics.testReturn >= 0 ? 'var(--success)' : 'var(--danger)'
                      }}
                      title={`Test: ${formatPercent(window.metrics.testReturn)}`}
                    />
                  </div>
                  <div className="bar-value">{formatPercent(window.metrics.testReturn)}</div>
                </div>
              ))}
            </div>
            <div className="chart-legend">
              <span className="legend-item">
                <span className="legend-dot" style={{ background: 'var(--text-muted)' }}></span>
                Train (In-sample)
              </span>
              <span className="legend-item">
                <span className="legend-dot" style={{ background: 'var(--success)' }}></span>
                Test (Out-of-sample)
              </span>
            </div>
          </div>
        )}

        {viewMode === 'metrics' && (
          <div className="wf-metrics-table">
            <table>
              <thead>
                <tr>
                  <th>Window</th>
                  <th>Train Return</th>
                  <th>Test Return</th>
                  <th>Train Sharpe</th>
                  <th>Test Sharpe</th>
                  <th>WF Ratio</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {results.windows.map((window, i) => (
                  <tr 
                    key={i}
                    className={`${i === results.bestWindow ? 'best' : ''} ${i === results.worstWindow ? 'worst' : ''}`}
                    onClick={() => setSelectedWindow(i)}
                  >
                    <td>W{i + 1}</td>
                    <td className={window.metrics.trainReturn >= 0 ? 'positive' : 'negative'}>
                      {formatPercent(window.metrics.trainReturn)}
                    </td>
                    <td className={window.metrics.testReturn >= 0 ? 'positive' : 'negative'}>
                      {formatPercent(window.metrics.testReturn)}
                    </td>
                    <td>{formatSharpe(window.metrics.trainSharpe)}</td>
                    <td>{formatSharpe(window.metrics.testSharpe)}</td>
                    <td className={window.metrics.wfRatio >= 0.5 ? 'positive' : 'warning'}>
                      {formatSharpe(window.metrics.wfRatio)}
                    </td>
                    <td>
                      {i === results.bestWindow && <span className="badge best">🏆 Best</span>}
                      {i === results.worstWindow && <span className="badge worst">⚠️ Worst</span>}
                      {i !== results.bestWindow && i !== results.worstWindow && (
                        <span className="badge">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Window Detail Panel */}
      {selectedWindow !== null && results.windows[selectedWindow] && (
        <div className="wf-detail-panel">
          <div className="detail-header">
            <h4>Window {selectedWindow + 1} Details</h4>
            <button onClick={() => setSelectedWindow(null)}>✕</button>
          </div>
          <div className="detail-content">
            <div className="detail-section">
              <h5>Training Period (In-Sample)</h5>
              <p>{formatDate(results.windows[selectedWindow].trainStart || 0)} - {formatDate(results.windows[selectedWindow].trainEnd || 0)}</p>
              <div className="detail-metrics">
                <span>Return: {formatPercent(results.windows[selectedWindow].metrics.trainReturn)}</span>
                <span>Sharpe: {formatSharpe(results.windows[selectedWindow].metrics.trainSharpe)}</span>
                <span>Win Rate: {(results.windows[selectedWindow].metrics.trainWinRate * 100).toFixed(1)}%</span>
              </div>
            </div>
            <div className="detail-section">
              <h5>Testing Period (Out-of-Sample)</h5>
              <p>{formatDate(results.windows[selectedWindow].testStart || 0)} - {formatDate(results.windows[selectedWindow].testEnd || 0)}</p>
              <div className="detail-metrics">
                <span>Return: {formatPercent(results.windows[selectedWindow].metrics.testReturn)}</span>
                <span>Sharpe: {formatSharpe(results.windows[selectedWindow].metrics.testSharpe)}</span>
                <span>Win Rate: {(results.windows[selectedWindow].metrics.testWinRate * 100).toFixed(1)}%</span>
              </div>
            </div>
            <div className="detail-section">
              <h5>Walk-Forward Efficiency</h5>
              <div className="wf-ratio-bar">
                <div 
                  className="wf-ratio-fill"
                  style={{ width: `${Math.min(results.windows[selectedWindow].metrics.wfRatio * 100, 100)}%` }}
                />
              </div>
              <p>Ratio: {formatSharpe(results.windows[selectedWindow].metrics.wfRatio)} 
                ({results.windows[selectedWindow].metrics.wfRatio >= 0.7 ? 'Good' : results.windows[selectedWindow].metrics.wfRatio >= 0.5 ? 'Acceptable' : 'Poor'})</p>
            </div>
          </div>
        </div>
      )}

      {/* Interpretation Guide */}
      <div className="wf-interpretation">
        <h4>📖 Interpretation Guide</h4>
        <ul>
          <li><strong>WF Ratio:</strong> Closer to 1.0 means strategy generalizes well. Below 0.5 suggests overfitting.</li>
          <li><strong>Consistency:</strong> Similar returns across windows indicate robust strategy.</li>
          <li><strong>Out-of-sample:</strong> Test period performance is the true measure of strategy viability.</li>
        </ul>
      </div>
    </div>
  )
}

export default WalkForwardVisualization